import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { adminRequest, listResource, unwrapItems } from "services/adminApi";

const initialDraft = {
  caption: "",
  mediaUrl: "",
  platforms: ["facebook", "instagram"],
  postType: "feed",
  scheduledFor: "",
};

const typeLabels = { feed: "منشور", story: "ستوري", reel: "ريل" };

export default function SocialPublisherPage() {
  const [draft, setDraft] = useState(initialDraft);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    try {
      setItems(unwrapItems(await listResource("social")));
    } catch (requestError) {
      setError(requestError.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setPlatform = (platform, checked) => {
    setDraft((current) => ({
      ...current,
      platforms: checked
        ? [...new Set([...current.platforms, platform])]
        : current.platforms.filter((item) => item !== platform),
    }));
  };

  const create = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const post = await adminRequest("/admin/social", {
        method: "POST",
        body: JSON.stringify({
          caption: draft.caption,
          mediaUrl: draft.mediaUrl,
          platforms: draft.platforms,
          postType: draft.postType,
        }),
      });
      if (draft.scheduledFor) {
        await adminRequest(`/admin/social/${post.postId}/schedule`, {
          method: "POST",
          body: JSON.stringify({ scheduledFor: new Date(draft.scheduledFor).toISOString() }),
        });
        setMessage("تمت جدولة المنشور بتوقيت جهازك.");
      } else {
        setMessage("تم حفظ المسودة. لن تُنشر حتى تختار النشر أو الجدولة.");
      }
      setDraft(initialDraft);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const connectMeta = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await adminRequest("/admin/social/connect", { method: "POST", body: "{}" });
      window.location.assign(response.authorizationUrl);
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  };

  const publish = async (postId) => {
    if (!window.confirm("سيتم النشر الآن إلى الحسابات المختارة. هل تريد المتابعة؟")) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await adminRequest(`/admin/social/${postId}/publish`, { method: "POST", body: "{}" });
      setMessage("تم إرسال طلب النشر إلى المنصات المختارة.");
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3} dir="rtl">
        <Typography variant="h4" fontWeight="bold">
          النشر الاجتماعي
        </Typography>
        <Typography color="text" mb={3}>
          أنشئ مسودات، جدولة، وانشر من الخادم. رموز الوصول لا تظهر في لوحة الإدارة.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Typography>اربط صفحة Facebook وحساب Instagram مرة واحدة قبل أول نشر.</Typography>
            <Button variant="outlined" disabled={saving} onClick={connectMeta}>
              ربط Facebook وInstagram
            </Button>
          </Stack>
        </Paper>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <TextField
              select
              label="نوع المحتوى"
              value={draft.postType}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  postType: event.target.value,
                  platforms: event.target.value === "feed" ? current.platforms : ["instagram"],
                }))
              }
            >
              {Object.entries(typeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.platforms.includes("facebook")}
                    disabled={draft.postType !== "feed"}
                    onChange={(event) => setPlatform("facebook", event.target.checked)}
                  />
                }
                label="Facebook Page"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.platforms.includes("instagram")}
                    onChange={(event) => setPlatform("instagram", event.target.checked)}
                  />
                }
                label="Instagram"
              />
            </Stack>
            <TextField
              multiline
              minRows={4}
              label="النص"
              value={draft.caption}
              onChange={(event) =>
                setDraft((current) => ({ ...current, caption: event.target.value }))
              }
            />
            <TextField
              label="رابط الوسائط HTTPS"
              helperText={
                draft.postType === "story"
                  ? "للستوري: تصميم عمودي 1080×1920 (9:16)."
                  : draft.postType === "reel"
                  ? "للريل: رابط فيديو HTTPS عمودي."
                  : "مطلوب لمنشورات Instagram."
              }
              value={draft.mediaUrl}
              onChange={(event) =>
                setDraft((current) => ({ ...current, mediaUrl: event.target.value }))
              }
            />
            {draft.mediaUrl && (
              <Box
                component="img"
                src={draft.mediaUrl}
                alt="معاينة وسائط المنشور"
                sx={{
                  width: "100%",
                  maxWidth: 420,
                  maxHeight: 420,
                  objectFit: "cover",
                  borderRadius: 2,
                  border: "1px solid #E1ECEF",
                }}
              />
            )}
            <TextField
              label="الجدولة (اختياري)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={draft.scheduledFor}
              onChange={(event) =>
                setDraft((current) => ({ ...current, scheduledFor: event.target.value }))
              }
            />
            <Button variant="contained" disabled={saving} onClick={create}>
              حفظ {draft.scheduledFor ? "وجدولة" : "كمسودة"}
            </Button>
          </Stack>
        </Paper>
        <Stack spacing={1}>
          {items.map((post) => (
            <Paper key={post.postId} sx={{ p: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                <Box>
                  <Stack direction="row" spacing={1} mb={1}>
                    <Chip label={typeLabels[post.postType] || "منشور"} />
                    <Chip
                      label={post.status}
                      color={post.status === "published" ? "success" : "default"}
                    />
                  </Stack>
                  <Typography>{post.caption}</Typography>
                  {post.mediaUrl && (
                    <Box
                      component="img"
                      src={post.mediaUrl}
                      alt="صورة المنشور"
                      sx={{
                        display: "block",
                        width: 180,
                        height: 120,
                        mt: 1.5,
                        objectFit: "cover",
                        borderRadius: 2,
                        border: "1px solid #E1ECEF",
                      }}
                    />
                  )}
                  <Typography variant="caption" color="text">
                    {post.scheduledFor ? `مجدول: ${post.scheduledFor}` : "مسودة غير مجدولة"}
                  </Typography>
                </Box>
                {post.status !== "published" && (
                  <Button disabled={saving} onClick={() => publish(post.postId)}>
                    نشر الآن
                  </Button>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>
    </DashboardLayout>
  );
}
